import useProbateEstateCollection from 'lib/probate/probate-estate-collection'
import { ProbateEstate, ProbateEstateProperty } from 'lib/models/probate-estate'

import Part from '../components/part'
import { QUESTIONNAIRE_SECTIONS } from '../constants'
import Properties from './properties'
import PersonalPossessions from './personal-possessions'
import BankAccounts from './bank-accounts'
import PremiumBonds from './premium-bonds'
import Nsandi from './nsandi'
import PublicShareholdings from './public-shareholdings'
import Advisors from './advisors'
import LifeInsurance from './life-insurance'
import PrivatePensions from './private-pensions'
import StateBenefits from './state-benefits'
import useEstateItems from './use-estate-items'
import { useEstateProperties } from '../shared-contacts/estate-people'

type HandleSave = (event: { name: string; value: unknown }) => void

type Props = {
  probateEstate: ProbateEstate
  saveProbateEstateField: HandleSave
}

const Estate = ({ probateEstate, saveProbateEstateField }: Props) => {
  const { attributes } = probateEstate

  const {
    items: properties,
    createItem: createProperty,
    updateItem: updateProperty,
    deleteItem: deleteProperty,
    isCreating: isCreatingProperty,
    isLoaded: arePropertiesLoaded,
  } = useEstateProperties()

  const { forType } = useEstateItems({ probateEstateId: probateEstate.id })

  const advisors = useProbateEstateCollection({
    probateEstateId: probateEstate.id,
    collection: 'advisors',
    type: 'probate_estate_advisors',
  })
  const advisorsCollection = {
    ...advisors,
    createItem: () => advisors.createItem({}),
  }

  return (
    <Part
      id={QUESTIONNAIRE_SECTIONS.ESTATE.id}
      heading={QUESTIONNAIRE_SECTIONS.ESTATE.heading}
      description="Information on the estate's assets, such as property, bank accounts, and investments."
    >
      <Properties
        properties={properties as ProbateEstateProperty[]}
        createProperty={createProperty}
        updateProperty={updateProperty}
        deleteProperty={deleteProperty}
        isCreating={isCreatingProperty}
        isLoaded={arePropertiesLoaded}
      />
      <PersonalPossessions
        probateEstate={probateEstate}
        saveProbateEstateField={saveProbateEstateField}
        vehicleItems={forType('vehicle_or_possession')}
      />
      <BankAccounts
        collection={forType('bank_account')}
        sectionActive={attributes.bankAccountsSectionActive}
        saveProbateEstateField={saveProbateEstateField}
      />
      <PremiumBonds
        collection={forType('premium_bond')}
        sectionActive={attributes.premiumBondsSectionActive}
        saveProbateEstateField={saveProbateEstateField}
      />
      <Nsandi
        collection={forType('national_savings_and_investments')}
        sectionActive={attributes.nationalSavingsAndInvestmentsSectionActive}
        saveProbateEstateField={saveProbateEstateField}
      />
      <PublicShareholdings
        collection={forType('public_shareholding')}
        sectionActive={attributes.publicShareholdingsSectionActive}
        saveProbateEstateField={saveProbateEstateField}
      />
      <Advisors
        collection={advisorsCollection}
        sectionActive={attributes.advisorsSectionActive}
        saveProbateEstateField={saveProbateEstateField}
      />
      <LifeInsurance
        collection={forType('life_insurance')}
        sectionActive={attributes.lifeInsuranceSectionActive}
        saveProbateEstateField={saveProbateEstateField}
      />
      <PrivatePensions
        collection={forType('private_pension')}
        sectionActive={attributes.privatePensionsSectionActive}
        saveProbateEstateField={saveProbateEstateField}
      />
      <StateBenefits
        collection={forType('state_benefit')}
        sectionActive={attributes.stateBenefitsSectionActive}
        saveProbateEstateField={saveProbateEstateField}
      />
    </Part>
  )
}

export default Estate
